import {
  CourseMetadata,
  Lti1p3LaunchParams,
  OrchardEventPayload,
  UserEventV2,
} from "@backend-types";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useContext, useEffect, useMemo, useRef } from "react";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { uniqueArray } from "../../Helpers";
import { MacsAuthenticator } from "../../hooks/useMacsAuthenticator";
import { CourseStructureCtx, DbUser, UserCtx, UserSubmission } from "../../interfaces";
import {
  EventPayload,
  validateEventData,
  validateOrchardEventData,
} from "../../json/JSONValidation";
import { ErrorPage } from "../../pages/ErrorPage";
import { Question } from "../utils/CourseTree";
import { Lti1p3LaunchError } from "./Lti1p3Lxp";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflow: "hidden",
    position: "relative",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
  },
}));

interface Lti1p3SolverProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  launchParams: Lti1p3LaunchParams;
  launchError: Lti1p3LaunchError | null;
  currentQuestion: Question;
  currentUser: DbUser;
  userCtx: UserCtx;
  userCtxRef: React.MutableRefObject<UserCtx>;
  showFeedback: (status: "success" | "error") => void;
  handleTriggerRemediation: (skillId: string, triggered?: boolean) => void;
  handleIncrementInteraction: () => void;
}

export const Lti1p3Solver = (props: Lti1p3SolverProps) => {
  const {
    iframeRef,
    launchParams,
    launchError,
    currentQuestion,
    userCtxRef,
    currentUser,
    showFeedback,
    handleIncrementInteraction,
  } = props;
  const classes = useStyles();
  const lti1p3FormRef = useRef<HTMLFormElement>(null);
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;

  const courseMetadata = useMemo(() => {
    return courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata() as
      | CourseMetadata
      | undefined;
  }, [courseStructureCtx.courseTreeStructure]);

  useEffect(() => {
    const validateLaunchParams = (params: Lti1p3LaunchParams | null): boolean => {
      if (!params) return false;
      if (!params.oidcUrl) return false;
      return true;
    };

    if (validateLaunchParams(launchParams)) {
      lti1p3FormRef?.current?.submit();
    }
  }, [launchParams]);

  useEffect(() => {
    const handleMessage = async (
      event: MessageEvent<EventPayload | OrchardEventPayload>
    ) => {
      let payload = event.data as EventPayload | OrchardEventPayload;

      try {
        if (!payload || typeof payload !== "object") {
          throw new Error("Invalid payload received");
        }
        const isPayload = isEventPayload(payload);
        const isOrchardPayload = isOrchardEventPayload(payload);
        if (!isPayload && !isOrchardPayload) {
          throw new Error("Invalid payload received - Schema Invalid");
        }

        if (!isOrchardPayload) handleIncrementInteraction();
        const questionId = currentQuestion.id;
        const classId =
          MacsAuthenticator.authHook.studentCurrentClassId === null
            ? null
            : Number(MacsAuthenticator.authHook.studentCurrentClassId);
        // get the current attempt number
        const submissions = userCtxRef.current.userSubmissions.filter(
          (item) => item.question_id === questionId
        );
        let payloadMetadata: Object = {};
        let newSubmission: UserSubmission | undefined;
        const orchardMode = currentQuestion.activity_type === "orchard_node";

        const handleSubmission = async (points: number, maxPoints: number) => {
          // send the new submission to backend DB
          newSubmission = await userCtxRef.current.insertUserSubmission(
            questionId,
            points,
            maxPoints,
            undefined,
            undefined,
            true, // tells backend not to create a v1 user event if the points !== maxPoints
            orchardMode // orchard nodes don't need streak
          );

          if (newSubmission) {
            // immediately push the submission into the userCtx state to allow updating frontend
            const newUserSubmissions =
              uniqueArray(userCtxRef.current.userSubmissions) ?? [];
            const newQuestionSubmitted =
              uniqueArray(userCtxRef.current.questionsSubmitted) ?? [];

            newUserSubmissions.push(newSubmission);
            userCtxRef.current.setUserSubmissions([...newUserSubmissions]);
            userCtxRef.current.setQuestionsSubmitted([
              ...newQuestionSubmitted,
              questionId,
            ]);

            if (!orchardMode) await userCtxRef.current.deleteUserTryAgain(questionId);
            if (points !== maxPoints) {
              showFeedback("error");
            } else {
              showFeedback("success");
            }
          }
        };

        if (isPayload) {
          payload = event.data as EventPayload;
          console.log("Non-orchard event payload: ", payload);
          if (payload.type === "attempt_submitted") {
            const { points = 0, maxPoints = 10 } = payload;

            payloadMetadata = { points, maxPoints };
            await handleSubmission(points, maxPoints);
          }

          const transformedPayload = {
            // Tool provided
            type: payload.type,
            status: payload.status,
            contents: payload.contents,
            current_hint_level: payload.current_hint_level,
            total_hint_levels: payload.total_hint_levels,
            learning_objectives: payload.learning_objectives,
            skill_ids: payload.skill_ids,
            created_at: payload.created_at,
            // Digit controlled
            question_id: questionId,
            class_id: classId,
            submission_id: newSubmission?.id,
            attempt: submissions.length + 1,
            metadata: {
              ...payloadMetadata,
            },
          } as Omit<UserEventV2, "id" | "user_id">;

          userCtxRef.current.insertUserEventsV2(transformedPayload);
        } else if (isOrchardPayload) {
          payload = event.data as OrchardEventPayload;
          const payloadBody = payload.payload.request.body;
          console.log("Orchard event payload: ", payload);

          if (
            payload.type === "XAPI_CREATE_SUCCESS" &&
            payloadBody.verb.id === "http://adlnet.gov/expapi/verbs/completed"
          ) {
            console.log("Refreshing quiz scores");
            const nodeIdTokens = payloadBody.object.id.split("/");
            const nodeId = nodeIdTokens[nodeIdTokens.length - 1];
            // Immediately ingest the completion for the learning map if the event completion matches node ID
            if (
              nodeId === currentQuestion.problem_id ||
              nodeId === currentQuestion.template_id
            ) {
              console.log(
                "Generating submission for Orchard completion event on Learning Map: ",
                nodeId
              );
              await handleSubmission(10, 10);
            } else if (courseMetadata?.management_page?.show_submodule_progress) {
              // this is a different kind of completion, probably a quiz
              // only really need to capture it for the lesson progress bars in chemistry course
              const orchBody = payload.payload.request.body;
              const orchExt = orchBody.object.definition.extensions;
              const h5pType = orchExt["http://id.tincanapi.com/extension/h5p-type"] as
                | string
                | undefined;
              if (h5pType === "Question Set") {
                userCtxRef.current.softSlotQuizScore(
                  questionId,
                  currentQuestion.target_id,
                  payload
                );
              }
            }
          }
        }
      } catch (e) {
        const d = event.data as any;
        if (d && d.source && d.source === "react-devtools-bridge") {
        } else {
          console.error(e, event);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    currentQuestion.id,
    currentQuestion.problem_id,
    currentQuestion.template_id,
    currentQuestion.target_id,
    currentQuestion.activity_type,
    courseMetadata?.management_page?.show_submodule_progress,
    currentUser,
    userCtxRef,
    handleIncrementInteraction,
    showFeedback,
  ]);

  const isH5PLtiContent = (currentQuestion: Question) => {
    return currentQuestion.activity_type === "orchard_node_h5p";
  };
  return (
    <Box
      className={classes.root}
      style={{
        paddingTop: isH5PLtiContent(currentQuestion) ? "80px" : "0px",
        paddingLeft: isH5PLtiContent(currentQuestion) ? "24px" : "0px",
        paddingRight: isH5PLtiContent(currentQuestion) ? "24px" : "0px",
        backgroundColor: isH5PLtiContent(currentQuestion) ? "#f5f5f5" : "transparent",
      }}>
      {launchError ? (
        <ErrorPage title={launchError.title} description={launchError.description} />
      ) : (
        <>
          {launchParams && (
            <>
              <form
                ref={lti1p3FormRef}
                method="post"
                action={launchParams.oidcUrl}
                target="lti1p3iframe">
                {Object.entries(launchParams).map(([key, value]) => {
                  if (key !== "oidcUrl") {
                    return <input type="hidden" name={key} value={value} key={key} />;
                  } else {
                    return null;
                  }
                })}
              </form>
              <iframe
                // sandbox="allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-scripts allow-top-navigation allow-top-navigation-by-user-activation" // for FullStory ticket 5656
                allowFullScreen={true}
                title="Learning Activity"
                ref={iframeRef}
                className={classes.iframe}
                name="lti1p3iframe"></iframe>
            </>
          )}
          {launchParams === null && (
            <Box className={classes.iframe}>
              <Stack
                justifyContent="center"
                alignItems="center"
                direction="column"
                sx={{
                  width: "100%",
                  height: "100%",
                }}>
                <Stack justifyContent="center" alignItems="center" direction="column">
                  <CircularProgress color="primary" size={50} />
                  <Typography
                    component="h1"
                    variant="body1"
                    fontSize="2em"
                    fontWeight={700}
                    sx={{ marginTop: "21.44px", marginBottom: "21.44px" }}>
                    Launching your activity
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const isEventPayload = (item: any): item is EventPayload => {
  try {
    return validateEventData(item) ?? false;
  } catch (e) {
    return false;
  }
};

const isOrchardEventPayload = (item: any): item is OrchardEventPayload => {
  try {
    return validateOrchardEventData(item) ?? false;
  } catch (e) {
    return false;
  }
};
